import { Controller } from "@hotwired/stimulus"
import { Calendar } from "@fullcalendar/core"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import deLocale from "@fullcalendar/core/locales/de"

export default class extends Controller {
  static targets = ["calendar", "loading", "picker", "form", "startInput", "endInput", "submitButton", "overrideToggle"]

  static values = {
    bookingId: Number,
    currentUserName: String,
    startDate: String,
    endDate: String,
    selectOverlapAllowed: Boolean,
    timeTable: Object,
    events: Array,
    adminCanOverride: Boolean
  }

  connect() {
    this.addedEvent = false
    this.calendar = null

    this.initCalendar()
  }

  disconnect() {
    if (this.calendar) {
      this.calendar.destroy()
      this.calendar = null
    }
  }

  initCalendar() {
    this.showLoading()

    const timeTable = this.timeTableValue
    let events = [...this.eventsValue]

    if (this.hasBookingIdValue && this.bookingIdValue) {
      const existingEvent = events.find(item => item.id === this.bookingIdValue)
      if (existingEvent) {
        existingEvent.display = 'auto'
        existingEvent.title = this.currentUserNameValue
      }
    }
    const minDate = new Date()

    const calendarOptions = {
      plugins: [timeGridPlugin, interactionPlugin],
      initialView: window.innerWidth >= 765 ? 'timeGridWeek' : 'timeGridDay',
      validRange: { start: minDate },
      locale: deLocale,
      eventColor: '#016973',
      fixedWeekCount: true,
      nowIndicator: true,
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: ''
      },
      buttonText: { listWeek: 'Liste' },
      events: events,
      themeSystem: 'standard',
      snapDuration: '00:15:00',
      allDaySlot: false,
      editable: true,
      initialDate: this.startDateValue,
      selectOverlap: this.selectOverlapAllowedValue,
      selectable: true,
      selectAllow: (info) => info.start >= Date.now(),
      eventAllow: (info) => info.start >= Date.now(),
      businessHours: this.buildBusinessHours(timeTable),
      select: (selectionInfo) => this.handleSelect(selectionInfo),
      eventResize: (info) => this.updateInputs(info.event),
      eventDrop: (info) => this.updateInputs(info.event)
    }

    const preChecked = this.hasOverrideToggleTarget && this.overrideToggleTarget.checked
    if (!preChecked) {
      calendarOptions.selectConstraint = 'businessHours'
      calendarOptions.eventConstraint = 'businessHours'
    }

    this.calendar = new Calendar(this.calendarTarget, calendarOptions)

    setTimeout(() => {
      this.hideLoading()
      this.calendar.render()
    }, 500)
  }

  handleSelect(selectionInfo) {
    const existingNewEvent = this.calendar.getEventById('neu')
    if (existingNewEvent) {
      existingNewEvent.remove()
    }

    if (this.hasBookingIdValue && this.bookingIdValue) {
      const existingBookingEvent = this.calendar.getEventById(this.bookingIdValue)
      if (existingBookingEvent) {
        existingBookingEvent.remove()
      }
    }

    this.addedEvent = true
    const event = {
      id: 'neu',
      title: this.currentUserNameValue,
      start: selectionInfo.start,
      end: selectionInfo.end,
      allDay: false
    }
    this.updateInputs(event)
    this.calendar.addEvent(event)
  }

  removeSelection(event) {
    if (event) event.remove()

    if (this.hasBookingIdValue && this.bookingIdValue) {
      const existingEvent = this.calendar.getEventById(this.bookingIdValue)
      if (existingEvent) existingEvent.remove()
    }

    if (this.hasStartInputTarget) {
      const row = this.startInputTarget.closest('.row')
      if (row) row.style.display = 'none'
    }
  }

  updateInputs(event) {
    if (this.hasStartInputTarget) {
      this.startInputTarget.value = event.start.toLocaleString('de-DE')
    }
    if (this.hasEndInputTarget) {
      this.endInputTarget.value = event.end.toLocaleString('de-DE')
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.style.display = 'block'
    if (this.hasPickerTarget) this.pickerTarget.style.display = 'none'
    if (this.hasFormTarget) this.formTarget.classList.add('d-none')
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.classList.add('d-none')

  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.style.display = 'none'
    if (this.hasPickerTarget) this.pickerTarget.style.display = 'block'
    if (this.hasFormTarget) this.formTarget.classList.remove('d-none')
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.classList.remove('d-none')
  }

  addZeros(hour) {
    return hour === '' ? '0:00' : `${hour}:00`
  }

  toggleOverride() {
    if (!this.calendar || !this.adminCanOverrideValue) return

    const enabled = this.hasOverrideToggleTarget && this.overrideToggleTarget.checked
    if (enabled) {
      this.calendar.setOption('selectConstraint', null)
      this.calendar.setOption('eventConstraint', null)
    } else {
      this.calendar.setOption('selectConstraint', 'businessHours')
      this.calendar.setOption('eventConstraint', 'businessHours')
    }
  }

  buildBusinessHours(timeTable) {
    const days = [
      { nr: 1, name: 'mon' },
      { nr: 2, name: 'tue' },
      { nr: 3, name: 'wed' },
      { nr: 4, name: 'thu' },
      { nr: 5, name: 'fri' },
      { nr: 6, name: 'sat' },
      { nr: 7, name: 'sun' }
    ]

    const slots = []
    days.forEach(day => {
      for (let i = 0; i < 3; i++) {
        const startKey = `${day.name}_start`
        const endKey = `${day.name}_end`
        if (timeTable[startKey]?.[i] !== undefined) {
          slots.push({
            daysOfWeek: [day.nr],
            startTime: this.addZeros(timeTable[startKey][i]),
            endTime: this.addZeros(timeTable[endKey][i])
          })
        }
      }
    })
    return slots
  }
}
